import { Recency, type ListRowOptions } from '@beyond-js/ui';
import { Condition, Describe, type Capture, type Identity, type Names, type People, type Standing } from '@snapshots/domain';
import type { Language } from '@snapshots/locale';

type Text = (key: string, values?: Record<string, string | number>) => string;

/** The family's states (`Status`), whose shape tells them apart as well as their hue (C2) */
export const marks = { pending: 'running', completed: 'success', failed: 'failed', cancelled: 'stopped', deleted: 'attention' } as const;

/** What the rows say things with: the catalog, counts and moments, and who and where the snapshots name */
interface Words {
	text: Text;
	count: (key: string, count: number, values?: Record<string, string | number>) => string;
	relative: (value: string | Date | undefined) => string;
	date: (value: string | Date | undefined) => string;
	short: (value: string | Date | undefined) => string;
	language: Language;
	people: People;
	names: Names;
	identity?: Identity;
}

/**
 * The snapshots list's rows in the family's list (the list rules, D162; `@beyond-js/ui`'s `Collection` `row`,
 * `group` and `end`). Each snapshot is its state's mark before its title (L2), then one quiet line: its
 * state in words only when it is not Ready (Pending, Failed, Cancelled, Deleted with its date), where it
 * lives and who made it only where the rows of the page differ in them (L3), how many packages it holds and
 * how long ago it was made. Rows group by when they were made (`Recency`, L4), the order the service keeps,
 * and the list ends with how many it holds and that nothing is older (L5).
 *
 * A person's and a project's names may be learned after a row is drawn: the parts that say them are kept and
 * said again when the names arrive, until the next page replaces them.
 */
export class Entries {
	#words: Words;
	#scope: { project?: string; organization?: string };
	#recency: Recency;
	#differ = { author: false, project: false, organization: false };
	#mentions: Array<() => void> = [];
	#stops: Array<() => void>;

	constructor(words: Words, scope: { project?: string; organization?: string }) {
		this.#words = words;
		this.#scope = scope;
		this.#recency = new Recency({ locale: words.language, labels: Recency.labels[words.language] });
		const again = () => this.#mentions.forEach(say => say());
		this.#stops = [words.people.listen(again), words.names.listen(again)];
	}

	/** Takes a page's rows before they are drawn: what the rows share is not said on each (L3) */
	prepare(rows: Capture[]): void {
		const differ = (pick: (row: Capture) => string | undefined) => new Set(rows.map(pick)).size > 1;
		const several = (this.#words.identity?.organizations.length ?? 0) > 1;
		this.#mentions = [];
		this.#differ = {
			author: differ(row => row.author),
			project: !this.#scope.project && differ(row => row.project),
			organization: !this.#scope.project && !this.#scope.organization && several && differ(row => row.organization)
		};
	}

	/** One snapshot's `ListRow` options: its state's mark, and its quiet line */
	row(capture: Capture): Partial<ListRowOptions> {
		const { text, count } = this.#words;
		const standing: Standing = Condition.of(capture);
		const words = this.#standing(capture, standing);
		const deleted = capture.state === 'deleted';
		const meta = [
			standing === 'completed' ? null : words,
			deleted && capture.remover ? this.#mention(() => text('list.removed', { person: this.#person(capture.remover, capture.organization) })) : null,
			this.#differ.project ? this.#mention(() => this.#words.names.name(capture.project) ?? text('nav.project.unknown'), capture.project) : null,
			this.#differ.organization ? this.#mention(() => this.#words.names.name(capture.organization) ?? text('storage.unnamed'), capture.organization) : null,
			this.#differ.author && !deleted ? this.#mention(() => this.#person(capture.author, capture.organization)) : null,
			count('capture.packages.count', capture.packages.length),
			this.#moment(capture.created)
		].filter((part): part is string | HTMLElement => Boolean(part));
		return { lead: { state: marks[standing], label: words }, meta };
	}

	/** The group of a snapshot: when it was made, the order the list keeps */
	group(capture: Capture): { key: string; label: string } {
		return this.#recency.of(capture.created);
	}

	/** The list's end line: how many snapshots it holds, and that nothing is older */
	end({ count }: { count: number }): string {
		return this.#words.count('list.end', count);
	}

	/** The title a row is named by: its own, or one made from its packages and time */
	title(capture: Capture): string {
		return Describe.title(capture, this.#words.text, this.#words.short);
	}

	destroy(): void {
		this.#stops.forEach(stop => stop());
		this.#mentions = [];
	}

	#standing(capture: Capture, standing: Standing): string {
		const { text, date } = this.#words;
		return standing === 'deleted' ? text('capture.standing.deleted', { until: date(capture.recoverable) }) : text(`capture.standing.${standing}`);
	}

	/** A person by name and never by identifier: "You", their name, or "A member of {organization}" */
	#person(account: string | undefined, organization: string): string {
		const { text, people, names, identity } = this.#words;
		if (!account) return '';
		if (account === identity?.account) return text('person.you');
		const name = people.name(account);
		if (name) return name;
		const place = names.name(organization);
		return place ? text('person.member', { organization: place }) : text('person.someone');
	}

	/** A part of the line said again when names arrive; a project or organization carries its identifier as data */
	#mention(say: () => string, place?: string): HTMLElement {
		const node = document.createElement('span');
		if (place) node.dataset.place = place;
		const update = () => {
			node.textContent = say();
		};
		update();
		this.#mentions.push(update);
		return node;
	}

	/** "3 minutes ago", with the whole date on hover and for assistive technology */
	#moment(at: string): HTMLElement {
		const node = document.createElement('time');
		node.dateTime = at;
		node.title = this.#words.date(at);
		node.textContent = this.#words.relative(at);
		return node;
	}
}
